import React, { useEffect, useState } from "react";
import { getApiUrl, BASE_URL } from "../utils/api";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useFormik } from "formik";
import * as Yup from "yup";

const Bskuser = () => {
  const [orders, setOrders] = useState([]);
  const [user, setUser] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(true);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const ordersPerPage = 5;

  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);

  const guestId = localStorage.getItem("userId");

  // ---------- LOAD STATES ----------
  const loadStates = async () => {
    try {
      const res = await fetch(getApiUrl("state-ui-list"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });
      const data = await res.json();
      if (data.success) setStates(data.data);
    } catch {
      toast.error("Failed to load states");
    }
  };

  // ---------- LOAD USER ----------
  const loadUser = async () => {
    try {
      const res = await fetch(getApiUrl("user/get"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId: guestId }),
      });
      const data = await res.json();
      if (data.success) {
        setUser(data.data);

        // set districts for the state
        const match = data.data.state
          ? states.find((s) => s.state === data.data.state)
          : null;
        setDistricts(match ? match.districts : []);
      }
    } catch {
      toast.error("Failed to load user data");
    }
  };

  // ---------- LOAD ORDERS ----------
  const loadOrders = async (page = 1) => {
    try {
      const res = await fetch(getApiUrl("order-ui-list"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId: guestId, page, limit: ordersPerPage }),
      });
      const data = await res.json();
      if (data.success) {
        setOrders(data.data);
        setCurrentPage(data.pagination.page);
        setTotalPages(data.pagination.pages);
      } else {
        setOrders([]);
      }
    } catch {
      toast.error("Failed to load orders");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const init = async () => {
      await loadStates();
    };
    init();
  }, []);

  useEffect(() => {
    if (states.length) {
      loadUser();
      loadOrders(currentPage);
    }
  }, [states]);

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    loadOrders(page);
  };

  // ---------- FORM ----------
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: user?.name || "",
      phone: user?.phone || "",
      email: user?.email || "",
      state: user?.state || "",
      district: user?.district || "",
      address: user?.address || "",
      pincode: user?.pincode || "",
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Name is required"),
      phone: Yup.string()
        .matches(/^\d{10}$/, "Enter valid 10-digit phone")
        .required("Phone is required"),
      state: Yup.string().required("State is required"),
      district: Yup.string().required("District is required"),
      address: Yup.string().required("Address is required"),
      pincode: Yup.string()
        .matches(/^[1-9][0-9]{5}$/, "Enter valid 6-digit pincode")
        .required("Pincode is required"),
    }),
    onSubmit: async (values) => {
      try {
        const res = await fetch(getApiUrl("checkout-add"), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ...values, guestId }),
        });
        const data = await res.json();
        if (data.success) {
          toast.success(editMode ? "Details updated" : "Details saved");
          setUser(values);
          setEditMode(false);
        } else toast.error(data.message || "Failed to save");
      } catch {
        toast.error("Server error");
      }
    },
  });

  const handleStateChange = (e) => {
    const value = e.target.value;
    formik.setFieldValue("state", value);
    formik.setFieldValue("district", "");

    const match = states.find((s) => s.state === value);
    setDistricts(match ? match.districts : []);
  };

  if (loading)
    return (
      <div className="text-center mt-5">
        <div className="spinner-border"></div>
      </div>
    );

  return (
    <div className="container my-4">
      <ToastContainer />

      <h3 className="fw-bold mb-4">My Account</h3>

      {/* ORDERS */}
      <div className="card p-4 mb-4 shadow-sm border-0">
        <h5 className="mb-3 fw-semibold">My Orders</h5>
        {orders.length === 0 ? (
          <p className="text-muted">
            You haven’t placed any orders yet. Make an order with combo.
          </p>
        ) : (
          <>
            {orders.map((o, i) => (
              <div
                key={o._id || i}
                className="border rounded p-3 mb-3 shadow-sm"
              >
                {/* HEADER */}
                <div className="d-flex justify-content-between align-items-start mb-3">
                  <div>
                    <div
                      style={{
                        color: "#2563eb",
                        fontWeight: "600",
                        fontSize: "16px",
                      }}
                    >
                      Order ID: {o.orderUnique}
                    </div>
                    <small className="text-muted">
                      {new Date(o.createdAt).toLocaleDateString()} | ₹{o.amount}
                    </small>
                  </div>
                  <div className="d-flex gap-1">
                    <span
                      className={`badge px-2 py-1 ${
                        o.paymentStatus === "SUCCESS"
                          ? "bg-success"
                          : o.paymentStatus === "FAILED"
                            ? "bg-danger"
                            : "bg-warning text-dark"
                      }`}
                    >
                      {o.paymentStatus}
                    </span>
                    <span
                      className={`badge px-2 py-1 ${
                        o.shipStatus === "shipped"
                          ? "bg-primary"
                          : "bg-secondary"
                      }`}
                    >
                      {o.shipStatus?.charAt(0).toUpperCase() +
                        o.shipStatus?.slice(1)}
                    </span>
                  </div>
                </div>

                {/* PRODUCTS - IMAGE + SUBCATEGORY + DETAILS */}
                <div className="mb-3">
                  {o.products.map((p, idx) => (
                    <div
                      key={idx}
                      className="d-flex align-items-center py-2 border-bottom"
                    >
                      {/* PRODUCT IMAGE */}
                      <img
                        src={
                          p.productInfo?.mainImage
                            ? `${BASE_URL}${p.productInfo.mainImage}`
                            : `${BASE_URL}/image/default.jpg`
                        }
                        width="45"
                        height="45"
                        className="me-3 rounded shadow-sm"
                        style={{ objectFit: "cover", flexShrink: 0 }}
                        onError={(e) =>
                          (e.target.src = `${BASE_URL}/image/default.jpg`)
                        }
                        alt="Product"
                      />

                      {/* PRODUCT INFO */}
                      <div className="flex-grow-1 pe-3">
                        <div className="fw-semibold mb-1">
                          {p.productInfo?.productName || p.name}
                        </div>
                        <div className="mb-1">
                          <span className="badge bg-light text-dark px-2 py-1 me-2">
                            {p.productInfo?.subcategory || "Unknown"}
                          </span>
                          <span className="text-muted">
                            Size: <strong>{p.size}</strong>
                          </span>
                        </div>
                        <small className="text-muted">Qty: {p.qty}</small>
                      </div>

                      {/* PRICE */}
                      <div className="text-end">
                        <div className="fw-bold text-primary fs-5">
                          ₹{p.price}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* TOTAL */}
                <div className="text-end pt-3 border-top">
                  <div className="h5 fw-bold text-success mb-1">
                    ₹{o.amount}
                  </div>
                  <small className="text-muted">
                    Order #{o.merchantOrderId?.slice(-20)}
                  </small>
                </div>
              </div>
            ))}

            <div className="d-flex justify-content-center mt-3">
              <button
                className="btn btn-outline-primary me-2"
                disabled={currentPage === 1}
                onClick={() => handlePageChange(currentPage - 1)}
              >
                Prev
              </button>

              <span className="align-self-center mx-2">
                Page {currentPage} of {totalPages}
              </span>

              <button
                className="btn btn-outline-primary ms-2"
                disabled={currentPage === totalPages}
                onClick={() => handlePageChange(currentPage + 1)}
              >
                Next
              </button>
            </div>
          </>
        )}
      </div>

      {/* USER DETAILS */}
      {user && !editMode && (
        <div className="card p-4 shadow-sm border-0">
          <h5 className="mb-3 fw-semibold">User Details</h5>
          <p>
            <b>Name:</b> {user.name}
          </p>
          <p>
            <b>Phone:</b> {user.phone}
          </p>
          <p>
            <b>Email:</b> {user.email || "-"}
          </p>
          <p>
            <b>State:</b> {user.state}
          </p>
          <p>
            <b>District:</b> {user.district}
          </p>
          <p>
            <b>Address:</b> {user.address}
          </p>
          <p>
            <b>Pincode:</b> {user.pincode}
          </p>
          <button
            className="btn btn-warning mt-2"
            onClick={() => setEditMode(true)}
          >
            Edit
          </button>
        </div>
      )}

      {/* EDIT FORM */}
      {user && editMode && (
        <div className="card p-4 shadow-sm border-0">
          <h5 className="mb-3 fw-semibold">Edit Details</h5>
          <form onSubmit={formik.handleSubmit}>
            <div className="d-flex flex-wrap">
              <div className="col-md-6 col-12 mb-3">
                <input
                  type="text"
                  name="name"
                  className="form-control"
                  placeholder="Name"
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                {formik.touched.name && formik.errors.name && (
                  <small className="text-danger">{formik.errors.name}</small>
                )}
              </div>

              <div className="col-md-6 col-12 mb-3">
                <input
                  type="tel"
                  name="phone"
                  className="form-control"
                  placeholder="Phone"
                  value={formik.values.phone}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                {formik.touched.phone && formik.errors.phone && (
                  <small className="text-danger">{formik.errors.phone}</small>
                )}
              </div>

              <div className="col-md-6 col-12 mb-3">
                <select
                  name="state"
                  className="form-select"
                  value={formik.values.state}
                  onChange={handleStateChange}
                >
                  <option value="">Select State</option>
                  {states.map((s) => (
                    <option key={s._id} value={s.state}>
                      {s.state}
                    </option>
                  ))}
                </select>
                {formik.touched.state && formik.errors.state && (
                  <small className="text-danger">{formik.errors.state}</small>
                )}
              </div>

              <div className="col-md-6 col-12 mb-3">
                <select
                  name="district"
                  className="form-select"
                  value={formik.values.district}
                  onChange={formik.handleChange}
                  disabled={!districts.length}
                >
                  <option value="">Select District</option>
                  {districts.map((d) => (
                    <option key={d} value={d}>
                      {d}
                    </option>
                  ))}
                </select>
                {formik.touched.district && formik.errors.district && (
                  <small className="text-danger">
                    {formik.errors.district}
                  </small>
                )}
              </div>

              <div className="col-12 mb-3">
                <textarea
                  name="address"
                  className="form-control"
                  rows={3}
                  placeholder="Address"
                  value={formik.values.address}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                {formik.touched.address && formik.errors.address && (
                  <small className="text-danger">{formik.errors.address}</small>
                )}
              </div>

              <div className="col-md-6 col-12 mb-3">
                <input
                  type="text"
                  name="pincode"
                  className="form-control"
                  placeholder="Pincode"
                  value={formik.values.pincode}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                {formik.touched.pincode && formik.errors.pincode && (
                  <small className="text-danger">{formik.errors.pincode}</small>
                )}
              </div>

              <div className="col-md-6 col-12 mb-3">
                <input
                  type="email"
                  name="email"
                  className="form-control"
                  placeholder="Email"
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
              </div>

              <div className="col-12 text-center">
                <button type="submit" className="btn btn-primary me-2">
                  Save
                </button>
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => setEditMode(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default Bskuser;
